import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { commonVar } from "../../../../providers/common-var/common";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { ContactDetails } from "../contact-details";

@Component({
  selector: "general-contact-details",
  templateUrl: "general-contact-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeneralContactDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GeneralContactDetails),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GeneralContactDetails extends ContactDetails implements OnInit {
  tempFormValue = null;
  characterCount: number = 0;

  resourceTypeOptions = [];
  professionalRelationOptions = [];
  statusOptions = [];
  addressTypeOptions = [];
  genderOptions = [];
  phoneNumberOptions = [];
  languageOptions = [];
  allAddressTypeOptions = [];
  allPhoneNumberOptions = [];

  _volunteerAttributesAdded: boolean = false;

  datePlacementPreference: PlacementArray = ["bottom-left"];
  _contactIsEmployee: boolean = false;
  _contactIsVolunteer: boolean = false;
  _isAdministrator: boolean = false;
  _contactIsSystemUser: boolean = false;
  dateOfBirthMin: any = "";
  dateOfBirthMax: any = "";
  driversLicenseExpMin: any = "";
  driversLicenseExpMax: any = "";
  hireDateMin: any = "";
  hireDateMax: any = "";

  emailRegex = commonVar.EMAIL_REGEX;
  phoneMask = commonVar.PHONE_US_MASK;
  zipCodeMask = commonVar.ZIP_CODE_MASK;
  zipCodePattern = commonVar.ZIP_PATTERN;
  zipExtensionPattern = commonVar.ZIP_EXT_PATTERN;
  zipExtensionMask = commonVar.ZIP_CODE_EXTENSION_MASK;
  stateList = commonVar.STATE_LIST;

  currentEmailAddress: string;

  @Input() isEditMode: boolean = false;
  @Output() deletePhoneNumber = new EventEmitter<any>();
  @Output() deleteAddress = new EventEmitter<any>();

  @Input() set isAdministrator(value: boolean) {
    this._isAdministrator = value;
  }
  get isAdministrator() {
    return this._isAdministrator;
  }

  @Input() set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
  }
  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  get addresses() {
    return this.innerFormGroup.get("addresses") as FormArray;
  }

  get phoneNumbers() {
    return this.innerFormGroup.get("phoneNumbers") as FormArray;
  }

  get professionalRelation() {
    return this.innerFormGroup.get("professionalRelation") as FormControl;
  }

  get resourceType() {
    return this.innerFormGroup.get("resourceType") as FormControl;
  }

  get groupAffiliation() {
    return this.innerFormGroup.get("groupAffiliation") as FormControl;
  }

  get notes() {
    return this.innerFormGroup.get("notes") as FormControl;
  }

  get dateOfBirth() {
    return this.innerFormGroup.get("dateOfBirth") as FormControl;
  }

  get gender() {
    return this.innerFormGroup.get("gender") as FormControl;
  }
  get primaryLanguage() {
    return this.innerFormGroup.get("primaryLanguage") as FormControl;
  }

  get emailAddress() {
    return this.innerFormGroup.get("emailAddress") as FormControl;
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value;
    this.setContactInputs();
  }

  set volunteerAttributesAdded(value: boolean) {
    this._volunteerAttributesAdded = value;
  }

  get volunteerAttributesAdded() {
    return this._volunteerAttributesAdded;
  }

  get contactIsVolunteer() {
    return this._contactIsVolunteer;
  }

  set contactIsVolunteer(value: boolean) {
    this._contactIsVolunteer = value;
    this.setContactInputs();
  }

  set value(value: any) {
    if (value.phoneNumbers) {
      const phoneNumbersCount = value.phoneNumbers.length;
      let phoneNumbersControlsCount = this.phoneNumbers.controls.length;
      if (value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber) {
        value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber =
          value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber.replace(
            /^(\d{3})(\d{3})(\d{4}).*/,
            "($1)$2-$3"
          );
      }
      while (phoneNumbersControlsCount !== phoneNumbersCount) {
        this.addPhoneNumber(event);
        phoneNumbersControlsCount = this.phoneNumbers.controls.length;
        if (value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber) {
          value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber =
            value.phoneNumbers[
              phoneNumbersControlsCount - 1
            ].phoneNumber.replace(/^(\d{3})(\d{3})(\d{4}).*/, "($1)$2-$3");
        }
      }
    }

    if (value.addresses) {
      const addressesCount = value.addresses.length;

      for (let i = 0; i < value.addresses.length; i++) {
        let addressCtrl = value.addresses[i];
        if (addressCtrl) {
          if (typeof addressCtrl.zipExtension === "string") {
            addressCtrl.zipExtension = addressCtrl.zipExtension.trim();
          }
        }
        if (i + 1 !== addressesCount) {
          this.addAddress(event);
        }
      }
    }
    this.tempFormValue = value;
  }

  constructor(
    protected formBuilder: FormBuilder,
    private resourceManagementService: ResourceManagementService,
    private cd: ChangeDetectorRef
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      resourceType: [null, Validators.required],
      professionalRelation: [null, Validators.required],
      resourceStatus: [null, Validators.required],
      systemUser: [false],
      firstName: ["", Validators.required],
      middleInitial: [""],
      lastName: ["", Validators.required],
      preferredName: [""],
      emailAddress: [
        "",
        [Validators.pattern(this.emailRegex), Validators.maxLength(50)],
      ],
      groupAffiliation: [""],
      phoneNumbers: new FormArray([]),
      addresses: new FormArray([]),
      notes: [""],
    });

    //Patch Select Value from RM Service
    this.resourceManagementService
      .getLookUpValues()
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          this.resourceTypeOptions = response["ContactType"]
            .map((item) => {
              return {
                display: item.description,
                value: item.id,
              };
            })
            .filter((item) => {
              let backOffice = item.display === "Back Office";
              let communityResource = item.display === "Community Resource";
              let patientContact = item.display === "Patient Contact";

              return !(backOffice || communityResource || patientContact);
            });
          this.professionalRelationOptions = response[
            "ProfessionalRelationType"
          ].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });

          this.statusOptions = response["ResourceStatus"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });

          this.allAddressTypeOptions = response["AddressType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
          this.genderOptions = response["Gender"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
          this.allPhoneNumberOptions = response["PhoneNumberType"].map(
            (item) => {
              return {
                display: item.description,
                value: item.id,
              };
            }
          );
          this.languageOptions = response["Language"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
          this.phoneNumberOptions = this.allPhoneNumberOptions;
          this.addressTypeOptions = this.allAddressTypeOptions;
        },
        (err) => {},
        () => {
          // default professionalRelation to Active
          let professionalRelation = this.professionalRelationOptions.find(
            (x) => x.display === "Community"
          );
          let defaultProfessionalRelation = professionalRelation
            ? professionalRelation.value
            : null;
          this.innerFormGroup
            .get("professionalRelation")
            .setValue(defaultProfessionalRelation);
          this.innerFormGroup.get("professionalRelation").markAsTouched();

          // default resourceType to Active
          let resource = this.statusOptions.find((x) => x.display === "Active");
          let defaultResourceType = resource ? resource.value : null;
          this.innerFormGroup
            .get("resourceStatus")
            .setValue(defaultResourceType);
          this.innerFormGroup.get("resourceStatus").markAsTouched();

          this.innerFormGroup.patchValue(this.tempFormValue);

          this.resourceType.setValue(parseInt(this.tempFormValue.resourceType));
          this.groupAffiliation.setValue(this.tempFormValue.groupAffiliation);

          if (this.contactIsEmployee || this.contactIsVolunteer) {
            this.gender.setValue(this.tempFormValue.gender);
            this.primaryLanguage.setValue(this.tempFormValue.primaryLanguage);
            this.dateOfBirth.setValue(this.tempFormValue.dateOfBirth);
          }

          this.onChange(this.tempFormValue);
          this.onTouched();
        }
      );

    // initialize to 1 for new users
    this.addAddress(event);
    this.addPhoneNumber(event);

    let now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const tenYearsLater = {
      year: now.getFullYear() + 10,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.dateOfBirthMin = { year: 1900, month: 1, day: 1 };
    this.dateOfBirthMax = today;
    this.driversLicenseExpMin = today;
    this.driversLicenseExpMax = tenYearsLater;
    this.hireDateMin = eightyYearsAgo;
    this.hireDateMax = tenYearsLater;

    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.subscriptions.push(
      this.professionalRelation.valueChanges.subscribe((value) => {
        this.contactIsEmployee = value === 2;
      })
    );

    this.subscriptions.push(
      this.resourceType.valueChanges.subscribe((value) => {
        this.contactIsVolunteer = value === 5;
      })
    );

    this.subscriptions.push(
      this.notes.valueChanges.subscribe((value) => {
        this.characterCount = value ? value.length : 0;
      })
    );

    this.subscriptions.push(
      this.emailAddress.valueChanges.subscribe((value) => {
        this.resourceManagementService.changeEmailAddress(value);
      })
    );
  }

  ngOnInit() {
    this.resourceManagementService.systemUserEmailAddress.subscribe(
      (email) => (this.currentEmailAddress = email)
    );
  }

  valid(control: string) {
    return this.innerFormGroup.get(control).valid;
  }

  touched(control: string) {
    return this.innerFormGroup.get(control).touched;
  }

  validPN(index: number, control: string) {
    return this.phoneNumbers.at(index).get(control).valid;
  }

  touchedPN(index: number, control: string) {
    return this.phoneNumbers.at(index).get(control).touched;
  }

  validA(index: number, control: string) {
    return this.addresses.at(index).get(control).valid;
  }

  touchedA(index: number, control: string) {
    return this.addresses.at(index).get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid
      ? null
      : { generalDetails: { valid: false } };
  }

  // Address
  addAddress(event) {
    event.preventDefault();
    const group = this.formBuilder.group({
      id: [""],
      addressType: [null, Validators.required],
      address1: ["", Validators.required],
      address2: [""],
      city: ["", Validators.required],
      state: [null, Validators.required],
      zip: ["", [Validators.required, Validators.pattern(this.zipCodePattern)]],
      zipExtension: [""],
      deleted: [""],
    });

    this.addresses.push(group);
  }

  removeAddress(event, index, selectedAddress) {
    event.preventDefault();
    if (selectedAddress.value["id"] > 0) {
      selectedAddress.get("deleted").setValue(true);
      selectedAddress.get("addressType").clearValidators();
      selectedAddress.get("address1").clearValidators();
      selectedAddress.get("city").clearValidators();
      selectedAddress.get("state").clearValidators();
      selectedAddress.get("zip").clearValidators();
      selectedAddress.get("zipExtension").clearValidators();
      this.deleteAddress.emit(selectedAddress.value);
    }
    this.addresses.removeAt(index);
  }

  // Phone Number
  addPhoneNumber(event) {
    event.preventDefault();
    const group = this.formBuilder.group({
      id: [""],
      phoneNumber: ["", [Validators.required, Validators.minLength(13)]],
      phoneNumberExtension: [""],
      phoneNumberType: [null, Validators.required],
      deleted: [""],
    });

    this.phoneNumbers.push(group);
  }

  removePhoneNumber(event, index, selectedNumber) {
    event.preventDefault();
    if (selectedNumber.value["id"] > 0) {
      selectedNumber.get("deleted").setValue(true);
      selectedNumber.get("phoneNumber").clearValidators();
      selectedNumber.get("phoneNumberType").clearValidators();
      this.deletePhoneNumber.emit(selectedNumber.value);
    }
    this.phoneNumbers.removeAt(index);
  }

  // Volunteer
  addVolunteerAttributes() {
    if (!this.innerFormGroup.contains("dateOfBirth")) {
      this.innerFormGroup.addControl(
        "dateOfBirth",
        this.formBuilder.control(null)
      );
    }
    if (!this.innerFormGroup.contains("gender")) {
      this.innerFormGroup.addControl("gender", this.formBuilder.control(null));
    }
    if (!this.innerFormGroup.contains("primaryLanguage")) {
      this.innerFormGroup.addControl(
        "primaryLanguage",
        this.formBuilder.control(null)
      );
    }

    this.volunteerAttributesAdded = true;
  }

  setContactInputs() {
    if (this.contactIsEmployee || this.contactIsVolunteer) {
      if (this.contactIsEmployee) {
        this.addEmployeeAttributes();
      }
      this.addVolunteerAttributes();
    }

    if (!this.contactIsEmployee) {
      this.removeEmployeeAttributes();
      if (!this.contactIsVolunteer) {
        this.removeVolunteerAttributes();
      }
    }
  }

  removeVolunteerAttributes() {
    this.volunteerAttributesAdded = false;
    if (this.innerFormGroup.contains("dateOfBirth")) {
      this.innerFormGroup.removeControl("dateOfBirth");
    }
    if (this.innerFormGroup.contains("gender")) {
      this.innerFormGroup.removeControl("gender");
    }
    if (this.innerFormGroup.contains("primaryLanguage")) {
      this.innerFormGroup.removeControl("primaryLanguage");
    }
  }

  // Employee
  addEmployeeAttributes() {
    if (!this.innerFormGroup.contains("hireDate")) {
      this.innerFormGroup.addControl(
        "hireDate",
        this.formBuilder.control("", Validators.required)
      );
    }
    if (!this.innerFormGroup.contains("employeeAltId")) {
      this.innerFormGroup.addControl(
        "employeeAltId",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("driversLicenseNumber")) {
      this.innerFormGroup.addControl(
        "driversLicenseNumber",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("driversLicenseExpiration")) {
      this.innerFormGroup.addControl(
        "driversLicenseExpiration",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("payrollId")) {
      this.innerFormGroup.addControl("payrollId", this.formBuilder.control(""));
    }
  }

  removeEmployeeAttributes() {
    if (this.innerFormGroup.contains("hireDate")) {
      this.innerFormGroup.removeControl("hireDate");
    }
    if (this.innerFormGroup.contains("employeeAltId")) {
      this.innerFormGroup.removeControl("employeeAltId");
    }
    if (this.innerFormGroup.contains("driversLicenseNumber")) {
      this.innerFormGroup.removeControl("driversLicenseNumber");
    }
    if (this.innerFormGroup.contains("driversLicenseExpiration")) {
      this.innerFormGroup.removeControl("driversLicenseExpiration");
    }
    if (this.innerFormGroup.contains("payrollId")) {
      this.innerFormGroup.removeControl("payrollId");
    }
  }

  filterDeletedControls(controls: AbstractControl[]): AbstractControl[] {
    return controls.filter(
      (c) => c.value["deleted"] == false || c.value["deleted"] == ""
    );
  }

  validateEmailMandatory(event) {
    if (event.value) {
      this.innerFormGroup.controls["emailAddress"].setValidators([
        Validators.pattern(this.emailRegex),
        Validators.maxLength(50),
        Validators.required,
      ]);
    } else {
      this.innerFormGroup.controls["emailAddress"].setValidators([
        Validators.pattern(this.emailRegex),
        Validators.maxLength(50),
      ]);
    }
    this.contactIsSystemUser = event.value;
    this.innerFormGroup.updateValueAndValidity();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/contact-details/general/general-contact-details.ts